import { Alert, Empty, Flex, Skeleton, Spin, Timeline, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { PageWrapper } from '@/components/Shared/Wrapper';
import { StyledButton } from '@/lib/theme/components/Button';
import { companyUpdateError, companyUpdateListOptions } from './api';

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 16px 4px 0;

  .ant-timeline-item-content {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  h3 {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 22px;
  }
`;

const DashboardCompanyUpdates = ({ companyID }: { companyID?: number }) => {
  const { t } = useTranslation('companyUpdates');
  const companyUpdates = useQuery({
    ...companyUpdateListOptions(companyID ?? 0, {}),
    enabled: companyID !== undefined,
  });
  const records = companyUpdates.data?.data ?? [];

  return (
    <PageWrapper vertical style={{ height: 420, minHeight: 'unset', minWidth: 0, width: '100%', flex: 'none' }}>
      <Flex align="center" justify="space-between" gap={12} style={{ flexShrink: 0 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('pageTitle')}
        </Typography.Title>
        <Link to="/investor-relations/company-updates" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {t('viewAll')} <ArrowRightOutlined aria-hidden style={{ transform: 'none' }} />
        </Link>
      </Flex>
      <ScrollArea role="region" aria-label={t('pageTitle')} aria-busy={companyUpdates.isFetching} tabIndex={0}>
        {companyID === undefined ? (
          <Empty description={t('selectCompany')} />
        ) : companyUpdates.isPending ? (
          <div role="status" aria-label={t('loading')}>
            <Skeleton active paragraph={{ rows: 5 }} />
          </div>
        ) : companyUpdates.isError ? (
          <Alert
            type="error"
            showIcon
            role="alert"
            message={companyUpdateError(companyUpdates.error, t)}
            action={<StyledButton onClick={() => void companyUpdates.refetch()}>{t('retry')}</StyledButton>}
          />
        ) : records.length === 0 ? (
          <Empty description={t('empty')} />
        ) : (
          <Spin spinning={companyUpdates.isFetching}>
            <Timeline
              mode="alternate"
              aria-label={t('pageTitle')}
              items={records.map((companyUpdate) => ({
                key: `${companyID}:${companyUpdate.id}`,
                children: (
                  <div>
                    <Typography.Text style={{ fontSize: '14px', fontWeight: 700 }}>
                      <time dateTime={companyUpdate.date}>{companyUpdate.date}</time>
                    </Typography.Text>
                    <p>{companyUpdate.title}</p>
                  </div>
                ),
              }))}
            />
          </Spin>
        )}
      </ScrollArea>
    </PageWrapper>
  );
};

export default DashboardCompanyUpdates;
